import { Directive, inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApWindowService } from '@ap/cdk';
import { ApContentItem, ApContentService } from '@ap/content';
import { SessiebeheerResource, SessionStatus } from '@ap/interface-apic-sessiebeheer';
import { ApPegaResource, ApPegaStateService, PegaAnalyticsService, PegaFieldUpdates } from '@ap/pega';

import contentItems from './samen-rekening-openen.content.json';
import { ACTIVATION_CASE_TYPE_ID, AnalyticsReporter, CASE_DETAILS_DATA_PAGE_ID, CasePrefix } from './samen-rekening-openen.model';

@Directive()
export class SamenRekeningOpenenDirective implements OnInit, OnDestroy {
    public caseTypeId = ACTIVATION_CASE_TYPE_ID;
    public content: any;
    public title = '';

    private analyticsReporter: AnalyticsReporter;
    private currentCasePrefix: string;
    private searchParameters: URLSearchParams;
    private readonly subscription = new Subscription();
    private titleHasBeenUpdated = false;

    private readonly contentService: ApContentService = inject(ApContentService);
    private readonly pegaAnalyticsService: PegaAnalyticsService = inject(PegaAnalyticsService);
    private readonly pegaResource: ApPegaResource = inject(ApPegaResource);
    private readonly pegaStateService: ApPegaStateService = inject(ApPegaStateService);
    private readonly sessiebeheerResource: SessiebeheerResource = inject(SessiebeheerResource);
    protected readonly windowService: ApWindowService = inject(ApWindowService);

    public ngOnInit(): void {
        this.contentService.addContentItems(contentItems as ApContentItem);
        this.title = this.getTitle();
        this.setSearchParameters();

        this.content = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ActivationCode: this.searchParameters.get('activationCode') || undefined
        };
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public onCaseResumeOrChange(fullCaseId: string): void {
        this.updateCurrentCasePrefix(fullCaseId);
        this.setAnalyticsReporter();

        if (!this.titleHasBeenUpdated) {
            this.updateTitle(fullCaseId);
        }
    }

    public onAssignmentComplete(data: PegaFieldUpdates): void {
        if (this.analyticsReporter === AnalyticsReporter.FRONTEND_APPLICATION) {
            this.sendFormStepEvent(data);
        }
    }

    public logout(redirectUrl?: string): void {
        this.pegaStateService.clearState(this.caseTypeId, this.content);
        this.sessiebeheerResource.patchSession('0', { status: SessionStatus.TERMINATED }).subscribe(() => {
            if (redirectUrl) {
                this.windowService.location_href(redirectUrl);
            } else {
                this.windowService.location_href(this.contentService.get('samen-rekening-openen.logout'));
            }
        });
    }

    private sendFormStepEvent(pegaFieldUpdates: PegaFieldUpdates): void {
        this.pegaAnalyticsService.formStep('Koppelcode', 'Angular', pegaFieldUpdates, []);
    }

    private getTitle(): string {
        return this.contentService.get('samen-rekening-openen.title');
    }

    private updateTitle(fullCaseId: string): void {
        this.subscription.add(
            this.pegaResource
                .getData(CASE_DETAILS_DATA_PAGE_ID, {
                    InsKey: fullCaseId // eslint-disable-line @typescript-eslint/naming-convention
                })
                .pipe(map((pegaData) => pegaData.ProductName))
                .subscribe((productName) => {
                    if (productName) {
                        this.title = `${productName} ${this.getTitle().toLowerCase()}`;
                        this.titleHasBeenUpdated = true;
                    }
                })
        );
    }

    private updateCurrentCasePrefix(fullCaseId: string): void {
        const [, caseId] = fullCaseId.split(' ');
        const [casePrefix] = (caseId || '').split('-');

        this.currentCasePrefix = casePrefix;
    }

    private setAnalyticsReporter(): void {
        switch (this.currentCasePrefix) {
            case CasePrefix.ACTIVATION_JOURNEY:
            case CasePrefix.ORIENTATION:
                this.analyticsReporter = AnalyticsReporter.FRONTEND_APPLICATION;
                break;
            case CasePrefix.ORCHESTRATION:
            case CasePrefix.ONBOARD_NEW_CUSTOMER:
            default:
                this.analyticsReporter = AnalyticsReporter.PEGA;
                break;
        }
    }

    private setSearchParameters(): void {
        const location = new URL(window.location.href);
        this.searchParameters = new URLSearchParams(location.searchParams);
    }
}
